import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, type ErrorComponentProps } from "@tanstack/react-router"
import * as Sentry from "@sentry/react"
import { RiErrorWarningLine } from "@remixicon/react"

import "./routeErrorFallback.scss"

/**
 * 라우트 단위 오류 화면 — 화면 어디선가 렌더 중 예외가 나면 TanStack Router 기본 "Something went wrong!" 대신
 * 이 화면을 보여준다 (2026-09-28, stage React #185 이후 도입). 다시 시도(라우트 재렌더)·새로고침·홈으로 를 두고
 * 신고에 필요한 정보(오류 메시지·주소)를 함께 보여준다. 오류는 Sentry 로 보낸다.
 * I18nProvider 바깥(루트 라우트)에서도 그려질 수 있어 문구는 모두 기본값을 둔다.
 */
export default function RouteErrorFallback({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const message = error instanceof Error ? error.message : String(error)

  useEffect(() => {
    Sentry.captureException(error, { tags: { source: "route" } })
  }, [error])

  return (
    <div className="RouteErrorFallback" role="alert">
      <div className="RouteErrorFallback__card">
        <RiErrorWarningLine size={32} className="RouteErrorFallback__icon" />
        <h2 className="RouteErrorFallback__title">
          {t("routeError.title", "Something went wrong while showing this page")}
        </h2>
        <p className="RouteErrorFallback__desc">
          {t(
            "routeError.desc",
            "Try again or reload the page. If it keeps happening, send the details below to the team.",
          )}
        </p>
        <dl className="RouteErrorFallback__details">
          <dt>{t("routeError.message", "Error")}</dt>
          <dd>{message}</dd>
          <dt>{t("routeError.location", "Page")}</dt>
          <dd>{window.location.href}</dd>
        </dl>
        <div className="RouteErrorFallback__actions">
          <button type="button" className="RouteErrorFallback__btn" onClick={() => reset()}>
            {t("retry", "Retry")}
          </button>
          <button
            type="button"
            className="RouteErrorFallback__btn"
            onClick={() => window.location.reload()}>
            {t("routeError.reload", "Reload")}
          </button>
          <button
            type="button"
            className="RouteErrorFallback__btn RouteErrorFallback__btn--primary"
            onClick={() => navigate({ to: "/home" })}>
            {t("routeError.home", "Go to Home")}
          </button>
        </div>
      </div>
    </div>
  )
}
